import React, { useCallback, useMemo, useState } from 'react';
import {
  FlatList,
  Image,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import TonyteBackground from '../components/TonyteBackground';
import LoadingSpinner from '../components/LoadingSpinner';
import EventShareSheet from '../components/EventShareSheet';
import { FeedCardWrapper } from './FeedScreen';
import { useFeed } from '../hooks/useFeed';
import { useProfile } from '../hooks/useProfile';
import { usePromotionDetail } from '../hooks/usePromotions';
import { useCityContext } from '../context/CityContext';
import { auth } from '../firebase';
import { Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import type { TonyteTheme } from '../theme/themes';
import { useI18n } from '../i18n';
import type { RootStackParamList, TonyteActivity } from '../types';
import { getResolvedEventId } from '../utils/eventIds';
import { ANALYTICS_SOURCES, trackEventDetailOpen } from '../utils/eventAnalytics';
import type { ManageEventUser } from '../utils/eventPermissions';
import { formatPromotionDateRange } from '../utils/promotions';

const OUTFIT = Platform.select({ web: { fontFamily: 'Outfit, sans-serif' }, default: {} });

type Row = { key: string; items: TonyteActivity[] };

// A promotion's collection page: cover, copy, date range and its events —
// rendered with the feed's own card (interest, chat, calendar, share, manage
// actions all identical). Reached from the feed section or an old link; an
// ended/removed/empty collection shows a graceful "not available" state.
const PromotionScreen: React.FC = () => {
  const { t, language } = useI18n();
  const { theme } = useTheme();
  const themed = useMemo(() => createThemedStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const { width: viewportWidth } = useWindowDimensions();
  const columns = Platform.OS === 'web' && viewportWidth >= 1480 ? 3 : Platform.OS === 'web' && viewportWidth >= 1024 ? 2 : 1;
  const desktop = columns > 1;
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, 'Promotion'>>();
  const promotionId = route.params?.promotionId || '';
  const userId = auth.currentUser?.uid || null;
  const { profile } = useProfile(userId);
  const feedOptions = useMemo(() => ({ includeAdminOnlyCities: profile?.role === 'admin' }), [profile?.role]);
  const { activities, loading: feedLoading } = useFeed(feedOptions);
  const { selectedCityKey } = useCityContext();
  const detail = usePromotionDetail(promotionId, activities, feedLoading, selectedCityKey);
  const [shareSheetActivity, setShareSheetActivity] = useState<TonyteActivity | null>(null);

  const currentUser = useMemo<ManageEventUser | null>(
    () => (profile ? { ...profile, uid: userId } : userId ? { uid: userId } : null),
    [profile, userId],
  );

  const goBack = useCallback(() => {
    if (navigation.canGoBack()) navigation.goBack();
    else navigation.navigate('Main' as never);
  }, [navigation]);

  const openEvent = useCallback((activity: TonyteActivity) => {
    trackEventDetailOpen({ eventId: getResolvedEventId(activity), organizerId: activity.hostId, source: ANALYTICS_SOURCES.PROMOTION });
    navigation.navigate('EventDetail', { eventId: getResolvedEventId(activity), event: activity });
  }, [navigation]);
  const openChat = useCallback((activity: TonyteActivity) => {
    navigation.navigate('ChatDetail', {
      chatId: getResolvedEventId(activity),
      name: activity.title,
      kind: 'event',
      photo: activity.coverPhoto || activity.coverImageUrl || activity.imageUrl,
    });
  }, [navigation]);
  const editEvent = useCallback((activity: TonyteActivity) => {
    navigation.navigate('UploadEvent', { event: activity });
  }, [navigation]);
  const noop = useCallback(() => {}, []);

  const rows = useMemo<Row[]>(() => {
    if (detail.status !== 'ready') return [];
    const out: Row[] = [];
    for (let i = 0; i < detail.events.length; i += columns) {
      const chunk = detail.events.slice(i, i + columns);
      out.push({ key: `row-${chunk[0].id}`, items: chunk });
    }
    return out;
  }, [detail, columns]);

  const renderRow = useCallback(({ item }: { item: Row }) => {
    const cards = item.items.map(activity => (
      <View key={activity.id} style={desktop ? styles.gridItem : undefined}>
        <FeedCardWrapper
          activity={activity}
          userId={userId}
          currentUser={currentUser}
          onPress={openEvent}
          onOpenChat={openChat}
          onEdit={editEvent}
          canManageFeatured={false}
          onFeaturedPress={noop}
          onShareInApp={setShareSheetActivity}
        />
      </View>
    ));
    return desktop ? <View style={styles.gridRow}>{cards}</View> : <>{cards}</>;
  }, [currentUser, desktop, editEvent, noop, openChat, openEvent, userId]);

  const header = (
    <View style={[styles.topBar, { paddingTop: insets.top + 10 }, desktop && styles.desktopWidth]}>
      <TouchableOpacity
        style={[styles.backButton, themed.control]}
        onPress={goBack}
        accessibilityRole="button"
        accessibilityLabel={t('common.back', 'Back')}
        activeOpacity={0.8}
      >
        <Icon name="arrow-left" size={20} color={theme.colors.icon} />
      </TouchableOpacity>
      <Text style={[styles.topBarKicker, themed.textMuted, OUTFIT]}>{t('promotions.collection')}</Text>
    </View>
  );

  if (detail.status === 'loading') {
    return (
      <TonyteBackground appearanceAware style={[styles.container, themed.container]}>
        {header}
        <LoadingSpinner appearanceAware label={t('feed.loadingEvents')} />
      </TonyteBackground>
    );
  }

  if (detail.status === 'unavailable') {
    return (
      <TonyteBackground appearanceAware style={[styles.container, themed.container]}>
        {header}
        <View style={styles.unavailable}>
          <View style={[styles.unavailableIcon, themed.control]}>
            <Icon name="calendar" size={28} color={theme.colors.accentSecondary} />
          </View>
          <Text style={[styles.unavailableTitle, themed.textPrimary, OUTFIT]}>{t('promotions.unavailableTitle')}</Text>
          <Text style={[styles.unavailableText, themed.textSecondary, OUTFIT]}>{t('promotions.unavailableSub')}</Text>
          <TouchableOpacity style={styles.primaryButton} onPress={goBack} accessibilityRole="button" activeOpacity={0.85}>
            <Text style={[styles.primaryButtonText, OUTFIT]}>{t('promotions.backToEvents')}</Text>
          </TouchableOpacity>
        </View>
      </TonyteBackground>
    );
  }

  const { promotion, events } = detail;
  const dateRange = formatPromotionDateRange(promotion, language);

  const hero = (
    <View style={styles.hero}>
      <View style={[styles.cover, { height: desktop ? 300 : 210, borderColor: theme.colors.cardBorder }]}>
        <PromotionCover uri={promotion.coverImageUrl} dark={theme.isDark} />
      </View>
      <Text style={[styles.title, desktop && styles.titleDesktop, themed.textPrimary, OUTFIT]} accessibilityRole="header">{promotion.title}</Text>
      {promotion.subtitle ? <Text style={[styles.subtitle, themed.textSecondary, OUTFIT]}>{promotion.subtitle}</Text> : null}
      <View style={[styles.datesCard, themed.control]}>
        <Icon name="calendar" size={16} color={theme.colors.accent} />
        <View style={styles.datesCopy}>
          <Text style={[styles.datesTitle, themed.textPrimary, OUTFIT]}>{t('promotions.runs').replace('{range}', dateRange)}</Text>
          <Text style={[styles.datesNote, themed.textSecondary, OUTFIT]}>{t('promotions.dateRangeNote')}</Text>
        </View>
      </View>
      {promotion.description ? (
        <Text style={[styles.description, themed.textSecondary, OUTFIT]}>{promotion.description}</Text>
      ) : null}
      <Text style={[styles.eventsHeading, themed.textMuted, OUTFIT]}>
        {t('promotions.eventsCount').replace('{count}', String(events.length))}
      </Text>
    </View>
  );

  return (
    <TonyteBackground appearanceAware style={[styles.container, themed.container]}>
      {header}
      <FlatList
        data={rows}
        keyExtractor={row => row.key}
        renderItem={renderRow}
        ListHeaderComponent={hero}
        contentContainerStyle={[styles.list, desktop && styles.desktopWidth, { paddingBottom: insets.bottom + 40 }]}
        showsVerticalScrollIndicator={false}
        initialNumToRender={4}
        windowSize={7}
      />
      {shareSheetActivity && userId ? (
        <EventShareSheet
          activity={shareSheetActivity}
          userId={userId}
          userName={profile?.name ?? 'Traveler'}
          userPhotoURL={profile?.photo ?? undefined}
          visible={Boolean(shareSheetActivity)}
          onClose={() => setShareSheetActivity(null)}
        />
      ) : null}
    </TonyteBackground>
  );
};

const PromotionCover: React.FC<{ uri: string | null; dark: boolean }> = ({ uri, dark }) => {
  const [failed, setFailed] = useState(false);
  return (
    <>
      <LinearGradient
        colors={dark ? ['#1E2A44', '#3A2A4A', '#F27D26'] : ['#2C6E6D', '#8E6A4A', '#F27D26']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      {uri && !failed ? (
        <Image source={{ uri }} style={StyleSheet.absoluteFill} resizeMode="cover" onError={() => setFailed(true)} accessibilityIgnoresInvertColors />
      ) : (
        <View style={styles.coverFallback} pointerEvents="none">
          <Icon name="star" size={110} color="rgba(255,255,255,0.15)" />
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  topBar: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingBottom: 8 },
  backButton: { width: 40, height: 40, borderRadius: 14, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  topBarKicker: { fontSize: 11, fontWeight: Typography.extrabold, letterSpacing: 1.6, textTransform: 'uppercase' },
  desktopWidth: { width: '100%', maxWidth: 1440, alignSelf: 'center', paddingHorizontal: 24 },
  list: { paddingHorizontal: 16 },
  hero: { paddingTop: 6, paddingBottom: 14, gap: 10 },
  cover: { width: '100%', borderRadius: 24, overflow: 'hidden', borderWidth: 1 },
  coverFallback: { position: 'absolute', right: -6, bottom: -18, transform: [{ rotate: '-12deg' }] },
  title: { marginTop: 6, fontSize: 26, lineHeight: 31, fontWeight: Typography.extrabold, letterSpacing: -0.4 },
  titleDesktop: { fontSize: 34, lineHeight: 40 },
  subtitle: { fontSize: 15, lineHeight: 21, fontWeight: Typography.semibold },
  datesCard: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, borderRadius: 16, borderWidth: 1, padding: 12 },
  datesCopy: { flex: 1, gap: 2 },
  datesTitle: { fontSize: 14, fontWeight: Typography.extrabold },
  datesNote: { fontSize: 12, lineHeight: 17, fontWeight: Typography.medium },
  description: { fontSize: 15, lineHeight: 22 },
  eventsHeading: { marginTop: 8, fontSize: 11, fontWeight: Typography.extrabold, letterSpacing: 1.6, textTransform: 'uppercase' },
  gridRow: { flexDirection: 'row', gap: 16 },
  gridItem: { flex: 1, minWidth: 0, marginBottom: 16 },
  unavailable: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 32, gap: 10 },
  unavailableIcon: { width: 64, height: 64, borderRadius: 22, borderWidth: 1, alignItems: 'center', justifyContent: 'center', marginBottom: 6 },
  unavailableTitle: { fontSize: 20, fontWeight: Typography.extrabold, textAlign: 'center' },
  unavailableText: { fontSize: 14, lineHeight: 20, textAlign: 'center' },
  primaryButton: { marginTop: 10, borderRadius: 999, backgroundColor: '#F27D26', paddingHorizontal: 20, paddingVertical: 12 },
  primaryButtonText: { color: '#fff', fontSize: 14, fontWeight: Typography.extrabold },
});

const createThemedStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: { backgroundColor: theme.colors.background },
  textPrimary: { color: theme.colors.textPrimary },
  textSecondary: { color: theme.colors.textSecondary },
  textMuted: { color: theme.colors.textMuted },
  control: { backgroundColor: theme.colors.surfaceElevated, borderColor: theme.colors.border },
});

export default PromotionScreen;
